import React, { Dispatch, useEffect, useState } from 'react';
import { Typography } from '../../../primitives';
import enumToLabel from '../../../../utils/enumToLabel';
import { withStyles } from '@material-ui/core/styles';
import {
  Box,
  Grid,
  Divider,
  Switch,
  Hidden,
  Theme,
} from '@material-ui/core';
import { createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Transition } from '../../../confirmationDialog/ConfirmationDialog';
import TextField from '@material-ui/core/TextField';
import { IconButton } from '@material-ui/core';
import detailsPageStyles from '../../../commonStyles/detailsPage.style';
import classNames from 'classnames';
import { INTERACTION } from '../../Configuration';
import configPageStyles from '../../../commonStyles/configPage.style';
import { processSearchQuery } from '../searchQuery';
import Highlighter from 'react-highlight-words';
import { CurrentView, ModuleName } from '../Modules';

// Icons
import EditIcon from '../../../../utils/svgs/EditIcon';

// Redux
import { useSearchResultSections } from '../../useSearchResultSections';

interface Props {
  classes: { [key: string]: string };
  data: any;
  interaction?: INTERACTION;
  setPartialData: Dispatch<any>;
  currentView: CurrentView;
}

const initDialogData = {
  label: '',
  key: null,
};

const ProcessSteps: React.FC<Props> = ({
  classes,
  data,
  interaction,
  setPartialData,
  currentView,
}) => {
  const [processStepsData, setProcessStepsData] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogData, setDialogData] = useState<any>(initDialogData);

  const [searchResult, setSearchResult] = useState<string[]>([]);
  const [showWholeSection, setShowWholeSection] =
    useState<boolean>(true);

  const SECTION_TITLE = 'Process Steps Labels';

  const { searchQuery, updateSections } = useSearchResultSections(
    ModuleName.GENERAL,
    SECTION_TITLE,
  );

  useEffect(
    function setProcessStepData() {
      if (data && currentView !== CurrentView.SEARCH) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemLabel = item[1] as {
            label: string;
          };
          tmpData.push(itemLabel);
        }
        setProcessStepsData(tmpData);
      }
    },
    [data, currentView],
  );

  useEffect(
    function filterProcessStepDataBySearchResults() {
      if (currentView === CurrentView.SEARCH && data) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemLabel = item[1] as {
            label: string;
          };
          if (
            showWholeSection ||
            searchResult.some(
              (result) =>
                enumToLabelFunc(itemLabel.label)
                  .toLowerCase()
                  .includes(result.toLowerCase()) ||
                enumToLabelFunc(item[0])
                  .toLowerCase()
                  .includes(result.toLowerCase()),
            )
          ) {
            tmpData.push(itemLabel);
          }
        }
        setProcessStepsData(tmpData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchResult],
  );

  useEffect(
    function processSearch() {
      if (currentView === CurrentView.SEARCH) {
        //Show whole section is there are results in a title
        setShowWholeSection(
          processSearchQuery(searchQuery, [SECTION_TITLE]).length !==
            0,
        );

        const dynamicLabels: string[] = [];
        for (let item of Object.entries(data)) {
          const itemLabel = item[1] as {
            label: string;
          };
          dynamicLabels.push(enumToLabelFunc(itemLabel.label));
          dynamicLabels.push(enumToLabelFunc(item[0]));
        }

        const results = processSearchQuery(searchQuery, [
          SECTION_TITLE,
          ...dynamicLabels,
        ]);
        setSearchResult(results);

        updateSections(results);
      } else {
        setSearchResult([]);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, currentView],
  );

  const getElKey = (obj: any) => {
    const el = Object.entries(data).find(
      ([key, value]: any) => value === obj,
    );
    return el?.length ? el[0] : '';
  };

  const enumToLabelFunc = (txt?: string) => {
    if (!txt) {
      return '';
    } else if (txt === 'MANIFESTEDINBOUND') {
      return 'Manifested Inbound';
    } else if (txt === 'MANIFESTEDOUTBOUND') {
      return 'Manifested Outbound';
    }

    return enumToLabel(txt);
  };

  const onAfterDialogClose = () => {
    setShowDialog(false);
    setDialogData(initDialogData);
  };

  const renderDialog = () => {
    return (
      <Dialog
        classes={{ paperScrollPaper: classes.dialogRoot }}
        open={showDialog}
        TransitionComponent={Transition}
        onClose={() => {
          onAfterDialogClose();
        }}
      >
        <DialogTitle>
          <Typography>Edit</Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogRoot}>
          <TextField
            data-testid={`edit-label-${dialogData.label}`}
            autoFocus
            InputLabelProps={{
              shrink: true,
              className: classes.inputNameLabel,
            }}
            InputProps={{
              disableUnderline: true,
              className: classes.inputName,
            }}
            classes={{
              root: classes.inputNameHolder,
            }}
            value={dialogData.label}
            onChange={(e) => {
              setDialogData({
                ...dialogData,
                label: e.target.value,
              });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              onAfterDialogClose();
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid='edit-dialog-save-btn'
            onClick={() => {
              data[dialogData.key].label = dialogData.label;
              setPartialData({
                ...data,
              });
              onAfterDialogClose();
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderComponent = () => {
    return (
      <>
        {renderDialog()}
        <Grid item sm={12}>
          <Box>
            <Box
              className={classNames(
                classes.moduleTitle,
                classes.pLeft,
              )}
              mb={1}
              mt={2}
            >
              <Highlighter
                highlightClassName={classes.searchResultsMark}
                searchWords={searchResult}
                autoEscape={true}
                textToHighlight={SECTION_TITLE}
              />
            </Box>

            <Box mt={2}>
              <Box>
                <Grid
                  container
                  item
                  sm={12}
                  className={classes.textHeader}
                >
                  <Grid item sm={2} xs={7} className={classes.pLeft}>
                    Process Step
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={8}>
                      Label
                    </Grid>
                  </Hidden>
                  <Grid item sm={1} xs={2} />
                  <Grid item sm={1} xs={3}>
                    Active
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
              </Box>
              {processStepsData?.map(
                (partialDataEl: any, index: number, array: any[]) => {
                  return (
                    <Grid
                      container
                      item
                      sm={12}
                      key={partialDataEl.label}
                      className={classes.row}
                    >
                      <Grid
                        item
                        sm={2}
                        xs={7}
                        className={classNames(
                          classes.text,
                          classes.pLeft,
                        )}
                      >
                        <Highlighter
                          highlightClassName={
                            classes.searchResultsMark
                          }
                          searchWords={searchResult}
                          autoEscape={true}
                          textToHighlight={enumToLabelFunc(
                            getElKey(partialDataEl),
                          )}
                        />
                      </Grid>
                      <Hidden xsDown>
                        <Grid item sm={8} className={classes.text}>
                          <Highlighter
                            highlightClassName={
                              classes.searchResultsMark
                            }
                            searchWords={searchResult}
                            autoEscape={true}
                            textToHighlight={partialDataEl.label}
                          />
                        </Grid>
                      </Hidden>
                      <Grid item sm={1} xs={2}>
                        <IconButton
                          data-testid={`edit-btn-${enumToLabelFunc(
                            getElKey(partialDataEl),
                          )}`}
                          size='small'
                          className={classes.editIcon}
                          disabled={interaction === INTERACTION.READ}
                          onClick={() => {
                            setDialogData({
                              label: partialDataEl.label,
                              key: getElKey(partialDataEl),
                            });
                            setShowDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Grid>
                      <Grid
                        item
                        sm={1}
                        xs={3}
                        className={classes.muiSwitchHoler}
                      >
                        <Switch
                          data-testid={`switch-${enumToLabelFunc(
                            getElKey(partialDataEl),
                          )}`}
                          color='default'
                          size='small'
                          disabled={interaction === INTERACTION.READ}
                          className={
                            partialDataEl.active
                              ? classes.muiSwitch
                              : classes.muiSwitchOff
                          }
                          checked={partialDataEl.active}
                          onChange={() => {
                            data[getElKey(partialDataEl)].active =
                              !partialDataEl.active;
                            setPartialData({
                              ...data,
                            });
                          }}
                        />
                      </Grid>
                      {index !== array.length - 1 && (
                        <Grid item xs={12}>
                          <Divider className={classes.divider} />
                        </Grid>
                      )}
                    </Grid>
                  );
                },
              )}
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  return currentView === CurrentView.SEARCH && !searchResult.length
    ? null
    : renderComponent();
};

export default withStyles(
  createStyles((theme: Theme) => ({
    ...detailsPageStyles,
    ...configPageStyles(theme),
  })),
)(ProcessSteps);
